<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          Issue Claims for All Packages
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex h-full w-full overflow-y-auto">
        <div class="flex flex-col w-full my-2 px-3">
          <form
            action=""
            class="w-full flex flex-col space-y-4"
            @submit.prevent="createClaim"
          >
            <p>
              Create and issue claims for all packages for <b>{{ project.name }}</b>
            </p>

            <p>
              Claims will be issued for the period of <b>{{ DateTime.fromFormat(claimForm.form.month, 'yyyy-MM').toLocaleString() }}</b>

              to <b>{{ DateTime.fromFormat(claimForm.form.month, 'yyyy-MM').endOf('month').toLocaleString() }}</b>
            </p>

            <p
              class="bg-yellow-100 text-yellow-800 p-4 rounded-md text-sm"
            >
              Packages that do not have a claim for the previous month will not be included. Ensure that all subcontractors have a contact email set.
            </p>

            <label>
              Month
              <AppInput
                v-model="claimForm.form.month"
                name="month"
                type="month"
                class="form-input"
                :error="claimForm.getErrors('month')"
              />
            </label>
          </form>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <div>
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click.prevent="$emit('close')"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn--primary-blue ml-2"
            @click="createClaim"
          >
            Create
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { timeZone } from '@/helpers';
import Claim from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: Id;
  selectedMonth: DateTime;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const claimsStore = useClaimsStore();
const router = useRouter();

const claimForm = useFormData({
  month: props.selectedMonth.toFormat('yyyy-MM'),
  timeZone,
});

const massSendSubcontractorClaimAction = useStoreApiAction(claimsStore.massSendSubcontractorClaim);

const createClaim = () => {
  claimForm.resetErrors();

  massSendSubcontractorClaimAction.request(props.projectId, claimForm.form).then((claims) => {
    if(claims.length > 0) {
      Swal.fire({
        icon: 'success',
        title: 'Claims Issued',
      }).then(() => {
        emits('close');
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'No Claims Issued',
        text:
          'No claims were issued. Ensure that all subcontractors have a contact email set, and that a claim exists for the previous month. If it\'s the first claim for a package, please manually create it.',
      }).then(() => {
        emits('close');
      });
    }
  }).catch((error) => {
    if(error.status === 422) {
      claimForm.setErrors(error.data);
    }
  });
};

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});
</script>

<style scoped></style>
